import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { ArrowRight, Check, Clock, Tag } from "lucide-react";
import { useQuery } from "convex/react";
import { SiteLayout } from "./layout";
import { HeroImage } from "./hero-image";
import { Reveal } from "./reveal";
import {
  SERVICES,
  SERVICE_PAGES,
  SITE,
  allServicePages,
  extraLiveServicePages,
  mergeLiveServices,
  pageWithLiveContent,
  priceRangeLabel,
  servicePagePath,
  type LiveServiceRow,
  type ServicePageDef,
} from "@/lib/site";
import { api, BUSINESS_ID } from "@/lib/convex";

/**
 * Tjenesteside. Teksten under overskriften og lista «Hva inngår» kommer fra
 * Ascendly naar eieren har skrevet noe der, ellers fra den faste teksten i
 * src/lib/site.ts.
 *
 * Foer leste denne siden bare den faste teksten. Prisene fikk eieren endret
 * fra appen (forsiden, /priser og /book leser Convex), men beskrivelsen paa
 * f.eks. /premiumpakke-drammen sto urokkelig med den gamle teksten uansett
 * hva han skrev. For ham saa det ut som redigeringen ikke virket.
 *
 * `slug` er enten en fast side (SERVICE_PAGES) eller en tjeneste eieren har
 * lagt til selv (rute /tjenester/$slug). `fallback` er sistnevntes innhold
 * hentet paa serveren, slik at siden har tekst ogsaa foer nettleseren har
 * faatt svar fra Convex.
 */
export function ServicePage({ slug, fallback }: { slug: string; fallback?: ServicePageDef }) {
  const live = useQuery(api.bookings.listServicesForBusiness, {
    businessId: BUSINESS_ID,
  }) as LiveServiceRow[] | undefined;

  const services = useMemo(() => mergeLiveServices(SERVICES, live), [live]);
  const pages = useMemo(() => allServicePages(live), [live]);

  const page = useMemo<ServicePageDef | undefined>(() => {
    const statisk = SERVICE_PAGES.find((p) => p.slug === slug);
    if (statisk) return pageWithLiveContent(statisk, live);
    return extraLiveServicePages(live).find((p) => p.slug === slug) ?? fallback;
  }, [slug, live, fallback]);

  if (!page) return null;

  const others = pages.filter((p) => p.slug !== page.slug);
  // Pris og varighet vises bare for sider som handler om én konkret tjeneste.
  // Samlesidene (bilvask, bilpleie) peker paa en pakke for bookingknappen,
  // men skal ikke late som den prisen gjelder hele siden.
  const tjeneste =
    page.serviceId || page.dynamic ? services.find((s) => s.id === page.preselect) : undefined;
  const navnLiten = page.nav.toLowerCase();

  return (
    <SiteLayout>
      <section className="relative min-h-[70svh] flex items-end overflow-hidden">
        <HeroImage />
        <div className="relative z-10 mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-10 pb-16 sm:pb-24 pt-32">
          <Reveal>
            <div className="eyebrow text-primary/90">{SITE.name} · Drammen</div>
          </Reveal>
          <Reveal delay={140}>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[1] tracking-tight text-white mt-6 text-balance max-w-3xl">
              {page.h1}
            </h1>
          </Reveal>
          <Reveal delay={280}>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-white/85 whitespace-pre-line">{page.intro}</p>
          </Reveal>
          {tjeneste && (
            <Reveal delay={350}>
              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/75">
                <span className="inline-flex items-center gap-2">
                  <Tag className="h-4 w-4 text-primary" />
                  <span className="text-white">{priceRangeLabel(tjeneste)}</span>
                </span>
                <span className="inline-flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  ca. {tjeneste.durationMinutes} min
                </span>
              </div>
            </Reveal>
          )}
          <Reveal delay={420}>
            <Link
              to="/book"
              search={{ service: page.preselect } as never}
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-primary-strong px-7 py-3.5 text-sm font-medium text-primary-foreground shadow-luxe lift"
            >
              Book {navnLiten} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {page.points.length > 0 && (
        <section className="py-20 sm:py-28">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10">
            <Reveal>
              <div className="eyebrow">Hva inngår</div>
              <h2 className="font-serif text-3xl sm:text-4xl mt-4 text-balance">Slik gjør vi {navnLiten} i Drammen</h2>
            </Reveal>
            <ul className="mt-10 grid gap-4 sm:grid-cols-2">
              {page.points.map((p, i) => (
                <Reveal key={`${i}-${p}`} delay={i * 80}>
                  <li className="flex gap-3 rounded-xl border border-border bg-card p-5 text-sm text-foreground/90">
                    <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {p}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="py-16 border-t border-border bg-secondary/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="eyebrow">Andre tjenester</div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={servicePagePath(o) as any}
                className="rounded-xl border border-border bg-card p-5 hover:border-primary transition-colors flex items-center justify-between"
              >
                <span className="font-serif text-lg">{o.h1}</span>
                <ArrowRight className="h-4 w-4 text-primary" />
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/" className="text-sm text-muted-foreground hover:text-primary">← Tilbake til forsiden</Link>
          </div>
        </div>
      </section>

      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 gradient-radial" />
        <div className="relative mx-auto max-w-2xl px-4 sm:px-6 lg:px-10 text-center">
          <h2 className="font-serif text-3xl sm:text-5xl text-balance">Klar for {navnLiten}?</h2>
          <Link
            to="/book"
            search={{ service: page.preselect } as never}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary-strong px-7 py-3.5 text-sm font-medium text-primary-foreground shadow-luxe lift"
          >
            Book time <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
